import { useInfiniteQuery, useQueryClient } from 'react-query'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { CaretDown } from 'phosphor-react'
import { yupResolver } from '@hookform/resolvers/yup'
import clsx from 'clsx'

import { DialogModal } from '../../../../components/DialogModal'
import { FormGroup } from '../../../../components/FormElements/FormGroup'
import { Input } from '../../../../components/FormElements/Input'
import { InfiniteSelect } from '../../../../components/InfiniteSelect'
import { Button } from '../../../../components/Button'

import { ReactComponent as Add } from '../../../../assets/svgs/add.svg'
import { ReactComponent as Trash } from '../../../../assets/svgs/secondary-trash.svg'
import projectSchema from '../../../../validators/projectSchema'

import { api } from '../../../../services/api'
import {
  type ClientContactDomain,
  type ClientDomain,
} from '../../../../types/domain/client-domain'
import { type AssetDomain } from '../../../../types/domain/asset-domain'
import { type ProjectPersistence } from '../../../../types/persistence/project-persistence'
import { type AssetAnalystDomain } from '../../../../types/domain/asset-analyst-domain'
import { useProjectCreateMutation } from '../../../../services/hooks/projects/mutations/useProjectCreateMutation'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { handlerType } from '../../../../enumns/handlerType'
import { type ProjectDomain } from '../../../../types/domain/project-domain'
import { useProjectEditMutation } from '../../../../services/hooks/projects/mutations/useProjectEditMutation'
import { getErrorMessage } from '../../../../utils/getErrorMessage'
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from '../../../../constants/toast'
import { useRequestUpdateHasBecomeProjectMutation } from '../../../../services/hooks/request/mutations/useRequestUpdateHasBecomeProjectMutation'

type Props = {
  isOpen: boolean
  title: string
  type: handlerType
  data?: ProjectDomain
  onClose: () => void
  requestData?: ProjectPersistence
  requestId?: number
}

export const ProjectForm = ({
  isOpen,
  title,
  type,
  data,
  onClose,
  requestData,
  requestId,
}: Props) => {
  const { mutate: createProjectMutation, isLoading: isCreatingProject } =
    useProjectCreateMutation()
  const { mutate: editProjectMutation, isLoading: isEditingProject } =
    useProjectEditMutation()
  const { mutate: updateRequestHasBecomeProject } =
    useRequestUpdateHasBecomeProjectMutation()

  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [searchClient, setSearchClient] = useState('')
  const [searchAsset, setSearchAsset] = useState('')
  const [searchAnalyst, setSearchAnalyst] = useState('')
  const [searchRequester, setSearchRequester] = useState('')
  const [assetBox, setAssetBox] = useState(true)
  const [analystBox, setAnalystBox] = useState(true)
  const [assetTotal, setAssetTotal] = useState(0)
  const [analystTotal, setAnalystTotal] = useState(0)

  const { control, formState, handleSubmit, reset, getValues, watch } =
    useForm<ProjectPersistence>({
      resolver: yupResolver(projectSchema),
      mode: 'onChange',
      defaultValues:
        type === handlerType.EDIT
          ? {
              name: data?.name,
              clientId: data?.client?.id,
              requesterId: data?.requester?.id,
              description: data?.description,
              assetIds:
                data?.assets && data?.assets?.length
                  ? data?.assets.map((value) => ({ id: value.id }))
                  : [{ id: undefined }],
              analystIds:
                data?.analysts && data?.analysts.length > 0
                  ? data?.analysts.map((value) => ({ id: value.id }))
                  : [{ id: undefined }],
            }
          : {
              assetIds: [{ id: undefined }],
              analystIds: [{ id: undefined }],
            },
    })

  useEffect(() => {
    if (requestId) {
      if (requestData) {
        reset(requestData)
      } else {
        reset({
          name: '',
          analystIds: [{ id: undefined }],
          assetIds: [{ id: undefined }],
          clientId: undefined,
          description: '',
          requesterId: undefined,
        })
      }
    }
  }, [requestData, requestId])

  const watchClient = watch('clientId')

  // Get info -------------------------------------------------------------
  const {
    data: clients,
    hasNextPage: hasMoreClients,
    fetchNextPage: fetchMoreClients,
  } = useInfiniteQuery(
    ['client-select', searchClient],
    async ({ pageParam = 1 }) => {
      const url = `/clients?page=${pageParam}&search=${searchClient}&sort=name&order=ASC`
      const { data } = await api.get(url)
      return data
    },
    {
      getNextPageParam({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  const {
    data: contacts,
    hasNextPage: hasMoreContacts,
    fetchNextPage: fetchMoreContacts,
  } = useInfiniteQuery(
    ['requester-select', watchClient, searchRequester],
    async ({ pageParam = 1 }) => {
      const url = `/clients/${watchClient}/contacts?page=${pageParam}&search=${searchRequester}&sort=name&order=ASC`
      const { data } = await api.get(url)
      return data
    },
    {
      enabled: watchClient !== null && watchClient !== undefined,
      getNextPageParam({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  const {
    data: assets,
    hasNextPage: hasMoreAssets,
    fetchNextPage: fetchMoreAssets,
  } = useInfiniteQuery(
    ['asset-select', searchAsset],
    async ({ pageParam = 1 }) => {
      const url = `/assets?page=${pageParam}&search=${searchAsset}&sort=fullName&order=ASC`
      const { data } = await api.get(url)
      return data
    },
    {
      getNextPageParam({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  const {
    data: analysts,
    hasNextPage: hasMoreAnalysts,
    fetchNextPage: fetchMoreAnalysts,
  } = useInfiniteQuery(
    ['analyst-select', searchAnalyst],
    async ({ pageParam = 1 }) => {
      const url = `/asset-analysts?page=${pageParam}&search=${searchAnalyst}&sort=fullName&order=ASC`
      const { data } = await api.get(url)
      return data
    },
    {
      getNextPageParam({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  // Sort select list -------------------------------------------------------------
  const clientPages = clients?.pages || []
  const contactPages = contacts?.pages || []
  const assetPages = assets?.pages || []
  const analystPages = analysts?.pages || []

  const clientOptions = useMemo(() => {
    const allClients: ClientDomain[] =
      clientPages.reduce(
        (acc: ClientDomain[], { data }) => [...acc, ...data],
        []
      ) || []

    const options = allClients.map((client: ClientDomain) => ({
      label: client.name,
      value: String(client.id),
    }))

    const selectedClientId = data?.client?.id
    const clientAlreadySelected = allClients.some(
      ({ id }) => id === selectedClientId
    )

    if (selectedClientId && !clientAlreadySelected) {
      options.push({
        label: data.client.name,
        value: String(data.client.id),
      })
    }

    return options
  }, [clientPages, data?.client])

  const contactOptions = useMemo(() => {
    const allContacts: ClientContactDomain[] =
      contactPages.reduce(
        (acc: ClientContactDomain[], { data }) => [...acc, ...data],
        []
      ) || []

    const options = allContacts.map((contact: ClientContactDomain) => ({
      label: contact.name,
      value: String(contact.id),
    }))

    const selectedContactId = getValues('requesterId')
    const contactAlreadySelected = allContacts.some(
      ({ id }) => String(id) === String(selectedContactId)
    )
    const defaultRequester = data?.requester

    if (selectedContactId && !contactAlreadySelected && defaultRequester) {
      options.push({
        label: defaultRequester.name,
        value: String(defaultRequester.id),
      })
    }

    return options
  }, [contactPages, data?.requester])

  const assetOptions = useMemo(() => {
    const allAssets: AssetDomain[] =
      assetPages.reduce(
        (acc: AssetDomain[], { data }) => [...acc, ...data],
        []
      ) || []

    const options = allAssets.map((asset: AssetDomain) => ({
      label: asset.fullName,
      value: String(asset.id),
    }))

    const selectedAssets = data?.assets

    selectedAssets?.forEach((selectedAsset) => {
      const assetAlreadySelected = allAssets.some(
        ({ id }) => String(id) === String(selectedAsset.id)
      )

      if (!assetAlreadySelected) {
        options.push({
          label: selectedAsset.fullName,
          value: String(selectedAsset.id),
        })
      }
    })

    return options
  }, [assetPages, data?.assets])

  const analystOptions = useMemo(() => {
    const allAnalysts: AssetAnalystDomain[] =
      analystPages.reduce(
        (acc: AssetAnalystDomain[], { data }) => [...acc, ...data],
        []
      ) || []

    const options = allAnalysts.map((analyst: AssetAnalystDomain) => ({
      label: analyst.fullName,
      value: String(analyst.id),
    }))

    const selectedAnalysts = data?.analysts

    selectedAnalysts?.forEach((selectedAnalyst) => {
      const analystAlreadySelected = allAnalysts.some(
        ({ id }) => String(id) === String(selectedAnalyst.id)
      )

      if (!analystAlreadySelected) {
        options.push({
          label: selectedAnalyst.fullName,
          value: String(selectedAnalyst.id),
        })
      }
    })

    return options
  }, [analystPages, data?.analysts])

  // Add and remove a new element ------------------------------------------
  const {
    fields: fieldsAsset,
    append: appendAsset,
    remove: removeAsset,
  } = useFieldArray({
    control,
    name: 'assetIds',
  })

  const {
    fields: fieldsAnalyst,
    append: appendAnalyst,
    remove: removeAnalyst,
  } = useFieldArray({
    control,
    name: 'analystIds',
  })

  const handleAssetAppend = () => {
    setAssetTotal(assetTotal + 1)
    appendAsset({ id: undefined })
  }

  const handleAnalystAppend = () => {
    setAnalystTotal(analystTotal + 1)
    appendAnalyst({ id: undefined })
  }

  const handleCloseAndReset = () => {
    onClose()
    reset()
  }

  // Save Handler -------------------------------------------------------------------------
  const handleCreateProject = (data: ProjectPersistence) => {
    createProjectMutation(data, {
      onSuccess(createdProject) {
        handleCloseAndReset()
        queryClient.invalidateQueries(['projects'])

        !!requestId &&
          updateRequestHasBecomeProject(requestId, {
            onSuccess() {
              queryClient.invalidateQueries(['requests'])
            },
            onError(error) {
              toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
            },
          })

        toast('Projeto criado com sucesso', TOAST_SUCCESS_DEFAULT_OPTIONS)
        navigate(`/painel/projects/${createdProject.id}`)
      },
      onError(error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  // Edit Handler -------------------------------------------------------------------------
  const handleEditProject = (project: ProjectPersistence) => {
    if (data?.id) {
      editProjectMutation(
        { id: data.id, data: project },
        {
          onSuccess(editedProject) {
            queryClient.invalidateQueries(['projects'])
            queryClient.invalidateQueries(['project', data?.id])

            handleCloseAndReset()

            toast('Projeto editado com sucesso', TOAST_SUCCESS_DEFAULT_OPTIONS)
            navigate(`/painel/projects/${editedProject.id}`)
          },
          onError(error) {
            toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
          },
        }
      )
    }
  }

  useEffect(() => {
    if (isOpen && data && type === handlerType.EDIT) {
      reset({
        name: data.name,
        clientId: data.client?.id,
        requesterId: data.requester?.id,
        description: data.description,
        assetIds: data.assets.map((value) => ({ id: value.id })),
        analystIds: data.analysts.map((value) => ({ id: value.id })),
      })
    }
  }, [isOpen, data, type])

  return (
    <DialogModal
      isOpen={isOpen}
      title={title}
      onClose={handleCloseAndReset}
      styleBox={{ maxWidth: 620 }}
    >
      <form
        className="pt-8 pb-6 pl-3 pr-2"
        onSubmit={handleSubmit(
          type === handlerType.SAVE ? handleCreateProject : handleEditProject
        )}
      >
        <div className="max-h-[calc(100vh_-_200px)] overflow-y-auto scrollbar">
          <div className="max-w-[540px] mx-auto mr-6">
            <div className="max-w-[90%] mb-4">
              <Controller
                name="name"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { invalid, error },
                }) => (
                  <Input
                    label="Nome do projeto"
                    errorMessage={error?.message}
                    autoFocus
                    placeholder="Nome do projeto"
                    isValid={!invalid && !!error}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </div>

            <div className="max-w-[90%] mb-4">
              <Controller
                name="clientId"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { error },
                }) => {
                  return (
                    <FormGroup label="Cliente" error={error?.message}>
                      <InfiniteSelect
                        placeholder="Selecione o cliente"
                        searchInputPlaceholder="Buscar pelo cliente"
                        hasNextPage={hasMoreClients}
                        defaultValue={String(value)}
                        options={clientOptions}
                        onLoadMoreOptions={fetchMoreClients}
                        onValueChange={(value) => onChange(value)}
                        onSearch={setSearchClient}
                      />
                    </FormGroup>
                  )
                }}
              />
            </div>

            <div className="max-w-[90%] mb-4">
              <Controller
                name="requesterId"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { error },
                }) => (
                  <FormGroup label="Solicitante" error={error?.message}>
                    <InfiniteSelect
                      placeholder="Solicitante"
                      searchInputPlaceholder="Buscar pelo solicitante"
                      hasNextPage={hasMoreContacts}
                      defaultValue={String(value)}
                      options={contactOptions}
                      onLoadMoreOptions={fetchMoreContacts}
                      onValueChange={(value) => onChange(value)}
                      onSearch={(term) => setSearchRequester(term)}
                    />
                  </FormGroup>
                )}
              />
            </div>

            {fieldsAsset.length > 0 && (
              <div className="flex flex-col mb-4">
                {fieldsAsset.length > 1 && (
                  <div
                    className="flex items-center justify-between mb-4 cursor-pointer max-w-[90%]"
                    onClick={() => setAssetBox(!assetBox)}
                  >
                    <p className="text-sm font-medium ">Assets</p>
                    <CaretDown
                      className={clsx('transition transform ease-in-out', {
                        'rotate-180': assetBox,
                      })}
                    />
                  </div>
                )}

                {assetBox && (
                  <>
                    <div className="flex flex-col gap-4 mb-4">
                      {fieldsAsset.map((field, index) => (
                        <Controller
                          key={field.id}
                          name={`assetIds.${index}.id`}
                          control={control}
                          render={({
                            field: { value = '', onChange },
                            fieldState: { error },
                          }) => {
                            // LOGIC: Não renderizar o valor que está selecionado nos outros inputs.
                            const values = getValues('assetIds')?.filter(
                              (_, indexOfSelectedAssetId) => {
                                return indexOfSelectedAssetId !== index
                              }
                            )

                            const filteredAssetOptions = assetOptions?.filter(
                              ({ value }: { value: string }) => {
                                return !values?.some(({ id }) => {
                                  return String(id) === String(value)
                                })
                              }
                            )

                            return (
                              <FormGroup
                                label={fieldsAsset.length === 1 ? 'Asset' : ''}
                                error={error?.message}
                              >
                                <div className="flex justify-between gap-2">
                                  <div className="flex-1 max-w-[90%]">
                                    <InfiniteSelect
                                      placeholder="Selecione o asset"
                                      searchInputPlaceholder="Buscar pelo asset"
                                      hasNextPage={hasMoreAssets}
                                      defaultValue={String(value)}
                                      options={filteredAssetOptions}
                                      onLoadMoreOptions={fetchMoreAssets}
                                      onValueChange={(value) => onChange(value)}
                                      onSearch={setSearchAsset}
                                    />
                                  </div>

                                  {fieldsAsset.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => removeAsset(index)}
                                    >
                                      <Trash className="md:w-8 md:h-8 w-7 h-7 text-grey-700" />
                                    </button>
                                  )}
                                </div>
                              </FormGroup>
                            )
                          }}
                        />
                      ))}
                    </div>

                    <Button
                      type="button"
                      size="small"
                      variant="outline"
                      onClick={handleAssetAppend}
                    >
                      <Add />
                      Adicionar asset
                    </Button>
                  </>
                )}
              </div>
            )}

            {fieldsAnalyst.length > 0 && (
              <div className="flex flex-col mb-10">
                {fieldsAnalyst.length > 1 && (
                  <div
                    className="flex items-center justify-between mb-4 cursor-pointer max-w-[90%]"
                    onClick={() => setAnalystBox(!analystBox)}
                  >
                    <p className="text-sm font-medium ">Analistas</p>
                    <CaretDown
                      className={clsx('transition transform ease-in-out', {
                        'rotate-180': analystBox,
                      })}
                    />
                  </div>
                )}

                {analystBox && (
                  <>
                    <div className="flex flex-col gap-4 mb-4">
                      {fieldsAnalyst.map((field, index) => (
                        <Controller
                          key={field.id}
                          name={`analystIds.${index}.id`}
                          control={control}
                          render={({
                            field: { value = '', onChange },
                            fieldState: { error },
                          }) => {
                            // LOGIC: Não renderizar o valor que está selecionado nos outros inputs.
                            const values = getValues('analystIds')?.filter(
                              (_, indexOfSelectedAssetId) => {
                                return indexOfSelectedAssetId !== index
                              }
                            )

                            const filteredAnalystIds = analystOptions?.filter(
                              ({ value }: { value: string }) => {
                                return !values?.some(({ id }) => {
                                  return String(id) === String(value)
                                })
                              }
                            )

                            return (
                              <FormGroup
                                label={
                                  fieldsAnalyst.length === 1 ? 'Analista' : ''
                                }
                                error={error?.message}
                              >
                                <div className="flex justify-between gap-2">
                                  <div className="flex-1 max-w-[90%]">
                                    <InfiniteSelect
                                      placeholder="Selecione o analista"
                                      searchInputPlaceholder="Buscra pelo analista"
                                      hasNextPage={hasMoreAnalysts}
                                      defaultValue={String(value)}
                                      options={filteredAnalystIds}
                                      onLoadMoreOptions={fetchMoreAnalysts}
                                      onValueChange={(value) => onChange(value)}
                                      onSearch={setSearchAnalyst}
                                    />
                                  </div>

                                  {fieldsAnalyst.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => removeAnalyst(index)}
                                    >
                                      <Trash className="md:w-8 md:h-8 w-7 h-7 text-grey-700" />
                                    </button>
                                  )}
                                </div>
                              </FormGroup>
                            )
                          }}
                        />
                      ))}
                    </div>

                    <Button
                      type="button"
                      size="small"
                      variant="outline"
                      onClick={handleAnalystAppend}
                    >
                      <Add />
                      Adicionar analista
                    </Button>
                  </>
                )}
              </div>
            )}

            <div className="mb-10">
              <Controller
                name="description"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { error },
                }) => (
                  <FormGroup
                    label="Descritivo da demanda"
                    error={error?.message}
                  >
                    <textarea
                      className="max-w-[90%] w-full h-32 p-3 text-sm rounded-lg lg:text-base border-1 border-grey-500 placeholder:text-grey-500 placeholder:font-sans placeholder:text-sm"
                      placeholder="Escreva aqui"
                      value={value}
                      onChange={onChange}
                    />
                  </FormGroup>
                )}
              />
            </div>

            <div className="max-w-[90%]">
              <Button
                type="submit"
                fullWidth
                disabled={!formState.isValid}
                isLoading={isCreatingProject || isEditingProject}
              >
                Salvar
              </Button>
            </div>
          </div>
        </div>
      </form>
    </DialogModal>
  )
}
